import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { TextButton } from '@components/Button/variants';
import useAwarnessBarVisibility from '@hooks/useAwarnessBarVisibility';
import useElementsHeight from '@hooks/useElementsHeight';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import useWindowSize from '@hooks/useWindowSize';
import { AdvertisementBanner } from '@interfaces/homepage';
import { ClickTarget, ContentType, ImpressionElement, Page, click, impression } from '@tracking';
import { hasUserOptedInToTrackingConsent } from '@utils/consent';

const BannerAdvertisement = ({
  adScheduleId,
  currentPage,
  clickthroughUrl,
  header,
  id,
  isContentPaywalled,
  shouldShowScrolledNav,
  shortHeader,
  sponsorLogo,
  sponsorName,
  trackingPixelUrl,
}: AdvertisementBanner) => {
  useAwarnessBarVisibility(shouldShowScrolledNav);
  const { width: windowWidth } = useWindowSize();
  const hydrated = useServerSideRendering();
  useElementsHeight({
    selectors: ['.top-sticky-banner', '#global-nav'],
    isScrollStyle: shouldShowScrolledNav,
    dependency: [windowWidth],
    cssVariable: 'nav-ad-banner-height',
  });

  const currentLocation = () => {
    switch (currentPage) {
      case Page.Article:
        return Page.Article;
      case Page.Briefing:
        return Page.Briefing;
      default:
        return Page.Homepage;
    }
  };

  const { trackEvent, Track } = useEventTracking({
    contentType: ContentType.Advertisement,
    contentId: id,
    linkedAdScheduleId: adScheduleId,
    location: currentLocation(),
  });

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.FeaturedPartnerAd }));
        }}
      >
        <aside
          className={clsx(
            'relative mb-20 h-full sm:!top-[calc(var(--nav-ad-banner-height,0px))] sm:border-none sm:px-28 sm:py-0',
            {
              ' !top-[calc(var(--nav-ad-banner-height,0px)+25px)] z-[-1] bg-grayscale-beige':
                [Page.Article, Page.Briefing].includes(currentPage) && isContentPaywalled,
              '!top-0': currentPage === Page.Homepage,
            }
          )}
          data-testid="featured-partner-top-banner"
        >
          <div className="mx-auto flex h-full max-w-container-lg items-center justify-between bg-grayscale-beige px-12 py-4 sm:h-max">
            <div>
              <p className="m-0 inline-block pr-4 font-suisse-neue text-body-sm">
                <span className="inline sm:hidden">{shortHeader}</span>
                <span className="hidden sm:inline">{header}</span>
              </p>
              <TextButton
                as="externalLink"
                className="!block font-suisse-intl text-disclosure-sm text-primary-magenta sm:!inline-block"
                href={clickthroughUrl}
                onClickCapture={() =>
                  trackEvent(
                    click({ clickTarget: ClickTarget.FeaturedPartnerAd, clickText: 'Learn more' })
                  )
                }
                rel="noreferrer sponsored"
              >
                Learn more
              </TextButton>
            </div>
            <a
              className="block items-center sm:flex"
              href={clickthroughUrl}
              onClickCapture={() =>
                trackEvent(
                  click({
                    clickTarget: ClickTarget.FeaturedPartnerAd,
                    clickText: 'Featured partner logo',
                  })
                )
              }
              rel="noreferrer sponsored"
              target="_blank"
            >
              <span className="block pb-6 text-right font-suisse-intl-mono text-overline-sm tracking-wide text-grayscale-gray sm:inline sm:pb-0">
                Featured Partner
              </span>
              <div className="relative">
                <LazyLoadImage
                  src={sponsorLogo}
                  alt={`${sponsorName} logo`}
                  className="float-right max-h-[44px] w-auto pl-12 align-middle"
                />
              </div>
            </a>
          </div>
          {hydrated && trackingPixelUrl && hasUserOptedInToTrackingConsent() && (
            <img src={trackingPixelUrl} className="hidden" />
          )}
        </aside>
      </InView>
    </Track>
  );
};

export default BannerAdvertisement;
